package one.healthium.log_food.ui.camera

import co.touchlab.kermit.Logger
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import loco.LOG_FOOD_SCAN_ERROR_IMAGE_TOO_LARGE
import loco.LOG_FOOD_SCAN_ERROR_NOT_FOOD
import loco.LOG_FOOD_SCAN_ERROR_QUOTA_EXCEEDED
import loco.LOG_FOOD_SCAN_ERROR_SERVICE_UNAVAILABLE
import one.healthium.loco.asLString
import one.healthium.log_food.domain.FoodRepository
import one.healthium.log_food.domain.FoodScanException
import one.healthium.log_food.domain.FoodScanException.ErrorCode
import one.healthium.log_food.ui.mappers.FoodUiMapper
import one.healthium.ui.common.event_producer.EventProducer
import one.healthium.ui.common.message.UiMessage
import one.healthium.ui.common.message.UiMessageManager
import one.healthium.ui.common.vm.BaseViewModel

class CameraViewModel(
    private val foodRepository: FoodRepository,
    private val foodUiMapper: FoodUiMapper,
) : BaseViewModel() {

    private val _state = MutableStateFlow(CameraUiState())
    val state = _state.asStateFlow()
    val uiMessage = UiMessageManager()
    val events = EventProducer<CameraEvent>()

    private val logger = Logger.withTag("CameraViewModel")

    fun onPhotoTaken(imageBytes: ByteArray, filePath: String?) {
        launch {
            try {
                _state.update {
                    it.copy(
                        isLoading = true,
                        frozenPhotoBytes = imageBytes.takeIf(ByteArray::isNotEmpty)?.let(StableByteArray::from),
                        frozenPhotoPath = filePath,
                    )
                }
                val foods = if (imageBytes.isNotEmpty()) {
                    foodRepository.scanFoodByPhoto(imageBytes)
                } else if (filePath != null) {
                    foodRepository.scanFoodByPhoto(filePath)
                } else {
                    logger.e { "onPhotoTaken: no image data available" }
                    _state.update { it.copy(isLoading = false, frozenPhotoBytes = null, frozenPhotoPath = null) }
                    return@launch
                }
                val uiModels = foods.map { foodUiMapper.toUiModel(it) }
                _state.update { it.copy(isLoading = false) }
                events.trigger(CameraEvent.FoodScanned(uiModels))
            } catch (e: Exception) {
                logger.e(e) { "onPhotoTaken failed" }
                _state.update { it.copy(isLoading = false, frozenPhotoBytes = null, frozenPhotoPath = null) }
                val message = if (e is FoodScanException) {
                    e.errorCode.toUiMessage()
                } else {
                    UiMessage.throwable(e)
                }
                uiMessage.add(message)
            }
        }
    }

    fun onPhotoCaptureFailed(exception: Throwable) {
        logger.e(exception) { "photo capture failed" }
        _state.update { it.copy(isLoading = false, frozenPhotoBytes = null, frozenPhotoPath = null) }
        uiMessage.add(UiMessage.throwable(exception))
    }

    private fun ErrorCode.toUiMessage(): UiMessage = when (this) {
        ErrorCode.NOT_FOOD -> UiMessage(message = loco.Res.string.LOG_FOOD_SCAN_ERROR_NOT_FOOD.asLString())
        ErrorCode.IMAGE_TOO_LARGE -> UiMessage(message = loco.Res.string.LOG_FOOD_SCAN_ERROR_IMAGE_TOO_LARGE.asLString())
        ErrorCode.QUOTA_EXCEEDED -> UiMessage(message = loco.Res.string.LOG_FOOD_SCAN_ERROR_QUOTA_EXCEEDED.asLString())
        ErrorCode.SERVICE_UNAVAILABLE -> UiMessage(message = loco.Res.string.LOG_FOOD_SCAN_ERROR_SERVICE_UNAVAILABLE.asLString())
        ErrorCode.UNKNOWN -> UiMessage(message = loco.Res.string.LOG_FOOD_SCAN_ERROR_SERVICE_UNAVAILABLE.asLString())
    }
}
